// @shared
// Клиентская утилита для мягких модульных 3D-персонажей поместо.
// Использует глобальный THREE, подключённый через CDN

export interface CapybaraNPC {
  group: any
  update: (t: number) => void
}

import type { PlayerAppearance } from "../shared/types"

declare const THREE: any

function softBox(width: number, height: number, depth: number, radius = 0.06): any {
  const safeRadius = Math.min(radius, width * 0.22, height * 0.22, depth * 0.22)
  const x = -width / 2
  const y = -height / 2
  const shape = new THREE.Shape()
  shape.moveTo(x + safeRadius, y)
  shape.lineTo(x + width - safeRadius, y)
  shape.quadraticCurveTo(x + width, y, x + width, y + safeRadius)
  shape.lineTo(x + width, y + height - safeRadius)
  shape.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height)
  shape.lineTo(x + safeRadius, y + height)
  shape.quadraticCurveTo(x, y + height, x, y + height - safeRadius)
  shape.lineTo(x, y + safeRadius)
  shape.quadraticCurveTo(x, y, x + safeRadius, y)
  const innerDepth = Math.max(0.001, depth - safeRadius * 2)
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: innerDepth,
    bevelEnabled: true,
    bevelSegments: 2,
    bevelSize: safeRadius,
    bevelThickness: safeRadius,
    curveSegments: 3,
    steps: 1,
  })
  geometry.translate(0, 0, -innerDepth / 2)
  return geometry
}

export interface CharacterAnchors {
  hat: any
  face: any
  piercing: any
  neck: any
  back: any
  outfit: any
  outerwear: any
  bottom: any
  shoes: any
  pet: any
  head: any
  leftArm: any
  rightArm: any
  leftLeg: any
  rightLeg: any
  hair: any
  baseClothingMaterials: any[]
  basePantsMaterials: any[]
  baseShoeMaterials: any[]
}

export interface CharacterParts {
  group: any
  body: any
  head: any
  hair: any
  leftEye: any
  rightEye: any
  leftArm: any
  rightArm: any
  leftLeg: any
  rightLeg: any
  leftShoe: any
  rightShoe: any
  pen: any
  note: any
  anchors: CharacterAnchors
}

interface CharacterBuild {
  bodyWidth: number
  bodyHeight: number
  bodyDepth: number
  armWidth: number
  armHeight: number
  legWidth: number
  legHeight: number
  headScale: number
  headY: number
  pantsColor: number
  shoeColor: number
  accessory:
    | "none"
    | "sunglasses"
    | "freckles"
    | "blush"
    | "scarf"
    | "backpack"
    | "glasses"
    | "beard"
    | "earrings"
    | "bow"
    | "ponytail"
    | "headband"
}

function getCharacterBuild(characterKey: string): CharacterBuild {
  switch (characterKey) {
    // --- МУЖСКИЕ ---
    case "sam":
      // Коренастый, крепкий. Шарф доступен отдельно в магазине и не должен
      // принудительно выступать из-под базовой футболки или рубашки.
      return {
        bodyWidth: 0.62,
        bodyHeight: 0.7,
        bodyDepth: 0.36,
        armWidth: 0.22,
        armHeight: 0.52,
        legWidth: 0.26,
        legHeight: 0.5,
        headScale: 1.02,
        headY: 1.48,
        pantsColor: 0x4a3a2a,
        shoeColor: 0x2a1a10,
        accessory: "none",
      }
    case "kai":
      // Высокий стройный, солнцезащитные очки
      return {
        bodyWidth: 0.5,
        bodyHeight: 0.78,
        bodyDepth: 0.3,
        armWidth: 0.17,
        armHeight: 0.64,
        legWidth: 0.22,
        legHeight: 0.64,
        headScale: 0.96,
        headY: 1.58,
        pantsColor: 0x101820,
        shoeColor: 0xffffff,
        accessory: "sunglasses",
      }
    case "max":
      // Крепкий, без бороды
      return {
        bodyWidth: 0.58,
        bodyHeight: 0.74,
        bodyDepth: 0.34,
        armWidth: 0.2,
        armHeight: 0.58,
        legWidth: 0.24,
        legHeight: 0.56,
        headScale: 1.0,
        headY: 1.52,
        pantsColor: 0x3a2a1a,
        shoeColor: 0x1f1410,
        accessory: "none",
      }
    case "alex":
      // Классический, очки-ботаник
      return {
        bodyWidth: 0.52,
        bodyHeight: 0.72,
        bodyDepth: 0.32,
        armWidth: 0.18,
        armHeight: 0.56,
        legWidth: 0.22,
        legHeight: 0.56,
        headScale: 1.0,
        headY: 1.5,
        pantsColor: 0x2d3a4a,
        shoeColor: 0x222222,
        accessory: "glasses",
      }

    // --- ЖЕНСКИЕ ---
    case "nova":
      // Миниатюрная, большая голова, румянец
      return {
        bodyWidth: 0.4,
        bodyHeight: 0.6,
        bodyDepth: 0.24,
        armWidth: 0.13,
        armHeight: 0.5,
        legWidth: 0.17,
        legHeight: 0.5,
        headScale: 1.14,
        headY: 1.48,
        pantsColor: 0xb46ac7,
        shoeColor: 0xd65c88,
        accessory: "blush",
      }
    case "mia":
      // Высокая стройная, серьги
      return {
        bodyWidth: 0.42,
        bodyHeight: 0.72,
        bodyDepth: 0.24,
        armWidth: 0.13,
        armHeight: 0.62,
        legWidth: 0.17,
        legHeight: 0.62,
        headScale: 0.98,
        headY: 1.58,
        pantsColor: 0x6b7fd1,
        shoeColor: 0xa24c88,
        accessory: "earrings",
      }
    case "lia":
      // С бантиком, веснушки
      return {
        bodyWidth: 0.4,
        bodyHeight: 0.66,
        bodyDepth: 0.24,
        armWidth: 0.13,
        armHeight: 0.54,
        legWidth: 0.17,
        legHeight: 0.54,
        headScale: 1.06,
        headY: 1.5,
        pantsColor: 0xe6a0c4,
        shoeColor: 0xd6446f,
        accessory: "bow",
      }
    case "eva":
      // Спортивная, с ободком
      return {
        bodyWidth: 0.42,
        bodyHeight: 0.68,
        bodyDepth: 0.24,
        armWidth: 0.14,
        armHeight: 0.58,
        legWidth: 0.18,
        legHeight: 0.58,
        headScale: 1.02,
        headY: 1.52,
        pantsColor: 0x2a6aa8,
        shoeColor: 0xf25a72,
        accessory: "headband",
      }

    default:
      return {
        bodyWidth: 0.5,
        bodyHeight: 0.7,
        bodyDepth: 0.3,
        armWidth: 0.18,
        armHeight: 0.55,
        legWidth: 0.22,
        legHeight: 0.55,
        headScale: 1.0,
        headY: 1.5,
        pantsColor: 0x2d3a4a,
        shoeColor: 0x222222,
        accessory: "glasses",
      }
  }
}

export function createCharacter(appearance: PlayerAppearance): CharacterParts {
  const group = new THREE.Group()

  const build = getCharacterBuild(appearance.characterKey)

  const skinMat = new THREE.MeshStandardMaterial({ color: appearance.skinColor, roughness: 0.86, metalness: 0 })
  const hairMat = new THREE.MeshStandardMaterial({ color: appearance.hairColor, roughness: 0.92, metalness: 0 })
  const clothesMat = new THREE.MeshStandardMaterial({ color: appearance.clothesColor, roughness: 0.88, metalness: 0 })
  const clothesColor = new THREE.Color(appearance.clothesColor)
  const clothesAccentMat = new THREE.MeshStandardMaterial({ color: clothesColor.clone().multiplyScalar(0.68), roughness: 0.9, metalness: 0 })
  const clothesLightMat = new THREE.MeshStandardMaterial({ color: clothesColor.clone().lerp(new THREE.Color(0xffffff), 0.72), roughness: 0.86, metalness: 0 })
  const pantsMat = new THREE.MeshStandardMaterial({ color: build.pantsColor, roughness: 0.9, metalness: 0 })
  const shoeMat = new THREE.MeshStandardMaterial({ color: build.shoeColor, roughness: 0.8, metalness: 0 })
  const clothesKey = appearance.clothesKey || "tshirt"

  // Базовый Y для ног / туловища рассчитывается от высоты ног,
  // чтобы персонаж всегда стоял ровно на полу.
  const legY = build.legHeight / 2 + 0.08
  const bodyY = legY + build.legHeight / 2 + build.bodyHeight / 2 - 0.02

  // Тело
  const bodyGeo = softBox(build.bodyWidth, build.bodyHeight, build.bodyDepth, 0.09)
  const body = new THREE.Mesh(bodyGeo, clothesMat)
  body.position.y = bodyY
  body.castShadow = true
  group.add(body)

  // Голова
  const headSize = 0.55 * build.headScale
  // softBox добавляет фаску наружу исходного размера. Для волос и шляп
  // используем реальный визуальный габарит головы, иначе аксессуар закрывает
  // только края номинального куба и оставляет центр макушки открытым.
  const headCornerRadius = Math.min(0.13, headSize * 0.22)
  const headVisualSize = headSize + headCornerRadius * 2
  const headGeo = softBox(headSize, headSize, headSize, 0.13)
  const head = new THREE.Mesh(headGeo, skinMat)
  const headY = bodyY + build.bodyHeight / 2 + headSize / 2 - 0.02
  head.position.y = headY
  head.castShadow = true
  group.add(head)

  // Глаза
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x292B33, roughness: 0.7 })
  const eyeGeo = new THREE.SphereGeometry(0.045, 12, 8)
  const eyeZ = headSize / 2 + 0.01
  const eyeOffsetX = headSize * 0.22
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat)
  leftEye.position.set(-eyeOffsetX, headY + 0.02, eyeZ)
  group.add(leftEye)
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat)
  rightEye.position.set(eyeOffsetX, headY + 0.02, eyeZ)
  rightEye.scale.z = 0.55
  group.add(rightEye)
  leftEye.scale.z = 0.55

  const smile = new THREE.Mesh(
    softBox(0.12, 0.025, 0.025, 0.01),
    new THREE.MeshStandardMaterial({ color: 0x8B5E4A, roughness: 0.8 }),
  )
  smile.position.set(0, headY - headSize * 0.2, eyeZ + 0.005)
  group.add(smile)

  // Аксессуар-персонаж
  // Шарф является частью выбранного персонажа, но под капюшоном создаёт
  // лишний пересекающийся воротник. Для худи его аккуратно скрываем.
  if (!(appearance.clothesKey === "hoodie" && build.accessory === "scarf")) {
    addAccessory(group, build.accessory, headY, headSize, eyeZ, eyeOffsetX, hairMat)
  }

  // Волосы (в зависимости от ключа причёски)
  const hair = buildHair(appearance.hairKey, appearance.gender, hairMat, headVisualSize)
  hair.position.y = headY
  group.add(hair)

  // Руки состоят из отдельной открытой кисти и рукава. Раньше под рукавом
  // оставалась целая кожаная рука: две вложенные beveled-геометрии пересекались
  // на сгибах и давали мерцание в движении, особенно на широких телах.
  const armX = build.bodyWidth / 2 + build.armWidth / 2 + 0.01
  const sleeveCoverage = clothesKey === "shirt"
    ? 0.8
    : clothesKey === "hoodie"
      ? 0.88
      : clothesKey === "dress"
        ? 0.32
        : 0.34
  const handOverlap = Math.min(0.018, build.armHeight * 0.035)
  const createArm = (x: number) => {
    const arm = new THREE.Group()
    arm.position.set(x, bodyY, 0)
    const skinHand = new THREE.Mesh(new THREE.BufferGeometry(), skinMat)
    const setSleeveBottom = (sleeveBottom: number) => {
      const exposedHeight = THREE.MathUtils.clamp(
        sleeveBottom + build.armHeight / 2,
        0,
        build.armHeight,
      )
      const handHeight = Math.min(build.armHeight, Math.max(0.035, exposedHeight + handOverlap))
      skinHand.geometry.dispose?.()
      skinHand.geometry = softBox(build.armWidth, handHeight, 0.2, Math.min(0.055, handHeight * 0.22))
      skinHand.position.set(0, -build.armHeight / 2 + handHeight / 2, 0)
      arm.userData.sleeveBottom = sleeveBottom
    }
    const baseSleeveBottom = build.armHeight * (0.5 - sleeveCoverage)
    arm.userData.skinHand = skinHand
    arm.userData.baseSleeveBottom = baseSleeveBottom
    arm.userData.setSleeveBottom = setSleeveBottom
    setSleeveBottom(baseSleeveBottom)
    arm.add(skinHand)
    group.add(arm)
    return arm
  }
  const leftArm = createArm(-armX)
  const rightArm = createArm(armX)

  // Ноги
  const legGeo = softBox(build.legWidth, build.legHeight, 0.22, 0.055)
  const legX = build.legWidth / 2 + 0.01
  const leftLeg = new THREE.Mesh(legGeo, pantsMat)
  leftLeg.position.set(-legX, legY, 0)
  group.add(leftLeg)

  const rightLeg = new THREE.Mesh(legGeo, pantsMat)
  rightLeg.position.set(legX, legY, 0)
  group.add(rightLeg)

  // Одежда v2. Все элементы строятся от размеров конкретного тела, а рукава
  // являются дочерними объектами рук и корректно следуют за анимацией.
  const addPart = (
    parent: any,
    material: any,
    width: number,
    height: number,
    depth: number,
    x: number,
    y: number,
    z: number,
    radius = 0.035,
  ) => {
    const mesh = new THREE.Mesh(softBox(width, height, depth, radius), material)
    mesh.position.set(x, y, z)
    parent.add(mesh)
    return mesh
  }
  const addSleeve = (arm: any, coverage: number, material = clothesMat, extra = 0.025) => {
    const height = build.armHeight * coverage
    return addPart(
      arm,
      material,
      build.armWidth + extra,
      height,
      0.2 + extra,
      0,
      build.armHeight / 2 - height / 2,
      0,
      Math.min(0.045, build.armWidth * 0.2),
    )
  }
  const frontZ = build.bodyDepth / 2 + 0.035

  if (clothesKey === "shirt") {
    addSleeve(leftArm, 0.8, clothesMat, 0.03)
    addSleeve(rightArm, 0.8, clothesMat, 0.03)
    addPart(group, clothesLightMat, build.bodyWidth * 0.44, 0.1, 0.035, 0, bodyY + build.bodyHeight * 0.41, frontZ, 0.018)
    addPart(group, clothesAccentMat, 0.035, build.bodyHeight * 0.72, 0.025, 0, bodyY - 0.02, frontZ + 0.018, 0.008)
    for (const offset of [0.2, 0.03, -0.14]) {
      addPart(group, clothesLightMat, 0.045, 0.045, 0.025, 0, bodyY + offset * build.bodyHeight, frontZ + 0.035, 0.012)
    }
    for (const arm of [leftArm, rightArm]) {
      addPart(arm, clothesAccentMat, build.armWidth + 0.045, 0.07, 0.23, 0, -build.armHeight * 0.31, 0, 0.018)
    }
  } else if (clothesKey === "hoodie") {
    addPart(group, clothesMat, build.bodyWidth + 0.08, build.bodyHeight + 0.05, build.bodyDepth + 0.07, 0, bodyY, 0, 0.075)
    addSleeve(leftArm, 0.88, clothesMat, 0.055)
    addSleeve(rightArm, 0.88, clothesMat, 0.055)
    addPart(group, clothesAccentMat, build.bodyWidth * 0.64, build.bodyHeight * 0.24, 0.045, 0, bodyY - build.bodyHeight * 0.2, frontZ + 0.045, 0.03)
    addPart(group, clothesAccentMat, build.bodyWidth + 0.09, 0.08, build.bodyDepth + 0.08, 0, bodyY - build.bodyHeight * 0.43, 0, 0.02)
    // Опущенный капюшон лежит на шее и плечах. Так он читается как часть
    // худи, но не превращается в жёсткую панель за головой и не закрывает
    // лицо или волосы.
    addPart(group, clothesAccentMat, headSize * 1.04, headSize * 0.3, headSize * 0.24, 0, headY - headSize * 0.56, -build.bodyDepth * 0.72, 0.055)
    addPart(group, clothesMat, headSize * 0.25, headSize * 0.19, headSize * 0.34, -headSize * 0.43, headY - headSize * 0.48, -build.bodyDepth * 0.25, 0.045)
    addPart(group, clothesMat, headSize * 0.25, headSize * 0.19, headSize * 0.34, headSize * 0.43, headY - headSize * 0.48, -build.bodyDepth * 0.25, 0.045)
    addPart(group, clothesLightMat, headSize * 0.82, headSize * 0.065, headSize * 0.09, 0, headY - headSize * 0.42, -build.bodyDepth * 0.18, 0.02)
    for (const x of [-0.09, 0.09]) {
      addPart(group, clothesLightMat, 0.025, build.bodyHeight * 0.28, 0.025, x, bodyY + build.bodyHeight * 0.18, frontZ + 0.055, 0.007)
    }
  } else if (clothesKey === "dress") {
    addSleeve(leftArm, 0.32, clothesMat, 0.035)
    addSleeve(rightArm, 0.32, clothesMat, 0.035)
    addPart(group, clothesLightMat, build.bodyWidth * 0.55, 0.065, 0.035, 0, bodyY + build.bodyHeight * 0.38, frontZ, 0.018)
    // Две ступени дают читаемый А-силуэт и закрывают верх брюк без тонких
    // совпадающих поверхностей.
    addPart(group, clothesMat, build.bodyWidth + 0.13, 0.24, build.bodyDepth + 0.1, 0, bodyY - build.bodyHeight * 0.48, 0, 0.06)
    addPart(group, clothesMat, build.bodyWidth + 0.25, 0.25, build.bodyDepth + 0.15, 0, bodyY - build.bodyHeight * 0.76, 0, 0.065)
    addPart(group, clothesAccentMat, build.bodyWidth + 0.06, 0.07, build.bodyDepth + 0.08, 0, bodyY - build.bodyHeight * 0.36, 0, 0.018)
  } else {
    // Футболка: короткие рукава, контрастная горловина и нижняя кромка.
    addSleeve(leftArm, 0.34, clothesMat, 0.035)
    addSleeve(rightArm, 0.34, clothesMat, 0.035)
    addPart(group, clothesAccentMat, build.bodyWidth * 0.52, 0.07, 0.035, 0, bodyY + build.bodyHeight * 0.42, frontZ, 0.018)
    addPart(group, clothesAccentMat, build.bodyWidth + 0.025, 0.055, build.bodyDepth + 0.025, 0, bodyY - build.bodyHeight * 0.45, 0, 0.014)
  }

  // Туфли
  const shoeGeo = softBox(build.legWidth + 0.04, 0.1, 0.3, 0.04)
  const leftShoe = new THREE.Mesh(shoeGeo, shoeMat)
  leftShoe.position.set(-legX, 0.04, 0.03)
  leftShoe.userData.standingPosition = leftShoe.position.clone()
  group.add(leftShoe)
  const rightShoe = new THREE.Mesh(shoeGeo, shoeMat)
  rightShoe.position.set(legX, 0.04, 0.03)
  rightShoe.userData.standingPosition = rightShoe.position.clone()
  group.add(rightShoe)

  // Ручка и тетрадь для письма — изначально скрыты
  const penGeo = new THREE.BoxGeometry(0.04, 0.04, 0.3)
  const penMat = new THREE.MeshToonMaterial({ color: 0x14c07a })
  const pen = new THREE.Mesh(penGeo, penMat)
  pen.visible = false
  group.add(pen)

  const noteGeo = new THREE.BoxGeometry(0.4, 0.03, 0.3)
  const noteMat = new THREE.MeshToonMaterial({ color: 0xf5f0dc })
  const note = new THREE.Mesh(noteGeo, noteMat)
  note.visible = false
  group.add(note)

  // ─── Anchors для аксессуаров ──────────────────────────
  // Эти Object3D являются точками привязки. Аксессуары добавляются как их
  // дочерние группы и автоматически наследуют трансформации тела.
  const anchors: CharacterAnchors = {
    hat: new THREE.Object3D(),
    face: new THREE.Object3D(),
    piercing: new THREE.Object3D(),
    neck: new THREE.Object3D(),
    back: new THREE.Object3D(),
    outfit: new THREE.Object3D(),
    outerwear: new THREE.Object3D(),
    bottom: new THREE.Object3D(),
    shoes: new THREE.Object3D(),
    pet: new THREE.Object3D(),
    head,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    hair,
    baseClothingMaterials: [clothesMat, clothesAccentMat, clothesLightMat],
    basePantsMaterials: [pantsMat],
    baseShoeMaterials: [shoeMat],
  }
  // Головные аксессуары строятся от центра головы. Автоматическая посадка
  // в accessoryBuilder учитывает реальный габарит головы и верх причёски.
  anchors.hat.position.set(0, headY, 0)
  // Лицо — у глаз, чуть впереди
  anchors.face.position.set(0, headY + 0.02, eyeZ)
  anchors.piercing.position.copy(anchors.face.position)
  // Шея — между головой и телом
  anchors.neck.position.set(0, headY - headSize / 2 + 0.04, 0)
  // Спина — позади туловища
  anchors.back.position.set(0, bodyY, -build.bodyDepth / 2)
  // Наряд — на туловище (центр)
  anchors.outfit.position.set(0, bodyY, 0)
  anchors.outerwear.position.copy(anchors.outfit.position)
  anchors.bottom.position.copy(anchors.outfit.position)
  anchors.shoes.position.copy(anchors.outfit.position)
  const bodyAccessoryFit = {
    x: build.bodyWidth / 0.5,
    y: build.bodyHeight / 0.7,
    z: build.bodyDepth / 0.3,
  }
  anchors.outfit.userData.accessoryFit = bodyAccessoryFit
  anchors.outerwear.userData.accessoryFit = bodyAccessoryFit
  anchors.bottom.userData.accessoryFit = bodyAccessoryFit
  anchors.shoes.userData.accessoryFit = bodyAccessoryFit
  // Head-attached outfit parts are authored against the same visual head
  // reference as hats. Scaling from the nominal 0.55 cube made the dragon
  // hood roughly 45% too large and turned the head into a square shell.
  const accessoryReferenceHeadSize = 0.792
  const outfitHoodClearance = 1.15
  anchors.head.userData.accessoryFit = {
    x: headVisualSize / accessoryReferenceHeadSize * outfitHoodClearance,
    y: headVisualSize / accessoryReferenceHeadSize * outfitHoodClearance,
    z: headVisualSize / accessoryReferenceHeadSize * outfitHoodClearance,
  }
  anchors.hat.userData.headwearFit = {
    headSize: headVisualSize,
    hairTop: hair.userData.headwearTop || headVisualSize * 0.64,
  }
  anchors.leftArm.userData.accessoryFit = anchors.rightArm.userData.accessoryFit = {
    x: build.armWidth / 0.18,
    y: build.armHeight / 0.55,
    // Глубина базовой руки одинакова у всех тел; уменьшение по ширине
    // утапливало рукав внутрь кожи на самых стройных персонажах.
    z: 1,
  }
  anchors.leftLeg.userData.accessoryFit = anchors.rightLeg.userData.accessoryFit = {
    x: build.legWidth / 0.22,
    y: build.legHeight / 0.55,
    z: 1,
  }
  // Питомец — рядом справа от персонажа на полу
  anchors.pet.position.set(0.7, 0, 0.1)
  for (const key of ["hat", "face", "piercing", "neck", "back", "outfit", "outerwear", "bottom", "shoes", "pet"] as const) {
    group.add(anchors[key])
  }

  group.traverse((child: any) => {
    if (child?.isMesh) child.castShadow = true
  })

  return {
    group,
    body,
    head,
    hair,
    leftEye,
    rightEye,
    leftArm,
    rightArm,
    leftLeg,
    rightLeg,
    leftShoe,
    rightShoe,
    pen,
    note,
    anchors,
  }
}

function addAccessory(
  group: any,
  accessory: CharacterBuild["accessory"],
  headY: number,
  headSize: number,
  eyeZ: number,
  eyeOffsetX: number,
  hairMat: any,
): void {
  if (accessory === "sunglasses") {
    const frameMat = new THREE.MeshToonMaterial({ color: 0x0a0a0a })
    const lensGeo = new THREE.BoxGeometry(0.16, 0.1, 0.04)
    const left = new THREE.Mesh(lensGeo, frameMat)
    left.position.set(-eyeOffsetX, headY + 0.03, eyeZ + 0.02)
    group.add(left)
    const right = new THREE.Mesh(lensGeo, frameMat)
    right.position.set(eyeOffsetX, headY + 0.03, eyeZ + 0.02)
    group.add(right)
    const bridgeGeo = new THREE.BoxGeometry(0.08, 0.025, 0.03)
    const bridge = new THREE.Mesh(bridgeGeo, frameMat)
    bridge.position.set(0, headY + 0.03, eyeZ + 0.02)
    group.add(bridge)
  } else if (accessory === "glasses") {
    const frameMat = new THREE.MeshToonMaterial({ color: 0x3a2a1a })
    const ringGeo = new THREE.BoxGeometry(0.16, 0.12, 0.03)
    const lensMat = new THREE.MeshBasicMaterial({
      color: 0xcfe8ff,
      transparent: true,
      opacity: 0.35,
    })
    const leftR = new THREE.Mesh(ringGeo, frameMat)
    leftR.position.set(-eyeOffsetX, headY + 0.02, eyeZ + 0.015)
    group.add(leftR)
    const rightR = new THREE.Mesh(ringGeo, frameMat)
    rightR.position.set(eyeOffsetX, headY + 0.02, eyeZ + 0.015)
    group.add(rightR)
    const lensGeo = new THREE.BoxGeometry(0.14, 0.1, 0.015)
    const leftL = new THREE.Mesh(lensGeo, lensMat)
    leftL.position.set(-eyeOffsetX, headY + 0.02, eyeZ + 0.03)
    group.add(leftL)
    const rightL = new THREE.Mesh(lensGeo, lensMat)
    rightL.position.set(eyeOffsetX, headY + 0.02, eyeZ + 0.03)
    group.add(rightL)
    const bridgeGeo = new THREE.BoxGeometry(0.08, 0.02, 0.02)
    const bridge = new THREE.Mesh(bridgeGeo, frameMat)
    bridge.position.set(0, headY + 0.02, eyeZ + 0.02)
    group.add(bridge)
  } else if (accessory === "blush") {
    const blushMat = new THREE.MeshBasicMaterial({ color: 0xff8fa8 })
    const cheekGeo = new THREE.BoxGeometry(0.08, 0.06, 0.02)
    const left = new THREE.Mesh(cheekGeo, blushMat)
    left.position.set(-eyeOffsetX - 0.05, headY - 0.08, eyeZ)
    group.add(left)
    const right = new THREE.Mesh(cheekGeo, blushMat)
    right.position.set(eyeOffsetX + 0.05, headY - 0.08, eyeZ)
    group.add(right)
  } else if (accessory === "scarf") {
    const scarfMat = new THREE.MeshToonMaterial({ color: 0xc84b3a })
    const scarfGeo = new THREE.BoxGeometry(headSize * 1.25, 0.15, headSize * 1.1)
    const scarf = new THREE.Mesh(scarfGeo, scarfMat)
    scarf.position.set(0, headY - headSize / 2 - 0.05, 0)
    group.add(scarf)
    const tailGeo = new THREE.BoxGeometry(0.12, 0.3, 0.08)
    const tail = new THREE.Mesh(tailGeo, scarfMat)
    tail.position.set(0.15, headY - headSize / 2 - 0.2, eyeZ - 0.1)
    group.add(tail)
  } else if (accessory === "beard") {
    const beardMat = new THREE.MeshToonMaterial({ color: 0x3a2818 })
    const beardGeo = new THREE.BoxGeometry(headSize * 0.85, 0.18, 0.08)
    const beard = new THREE.Mesh(beardGeo, beardMat)
    beard.position.set(0, headY - headSize * 0.32, eyeZ - 0.01)
    group.add(beard)
    const mustacheGeo = new THREE.BoxGeometry(headSize * 0.55, 0.05, 0.06)
    const mustache = new THREE.Mesh(mustacheGeo, beardMat)
    mustache.position.set(0, headY - headSize * 0.14, eyeZ)
    group.add(mustache)
  } else if (accessory === "earrings") {
    const earMat = new THREE.MeshBasicMaterial({ color: 0xf4d35e })
    const earGeo = new THREE.BoxGeometry(0.05, 0.1, 0.05)
    const left = new THREE.Mesh(earGeo, earMat)
    left.position.set(-headSize / 2 - 0.02, headY - 0.08, 0)
    group.add(left)
    const right = new THREE.Mesh(earGeo, earMat)
    right.position.set(headSize / 2 + 0.02, headY - 0.08, 0)
    group.add(right)
    // розовые губы
    const lipsMat = new THREE.MeshBasicMaterial({ color: 0xd94a6a })
    const lipsGeo = new THREE.BoxGeometry(0.12, 0.03, 0.02)
    const lips = new THREE.Mesh(lipsGeo, lipsMat)
    lips.position.set(0, headY - 0.12, eyeZ)
    group.add(lips)
  } else if (accessory === "bow") {
    // бантик на макушке + веснушки
    const bowMat = new THREE.MeshToonMaterial({ color: 0xff5a8a })
    const knotGeo = new THREE.BoxGeometry(0.08, 0.08, 0.08)
    const knot = new THREE.Mesh(knotGeo, bowMat)
    knot.position.set(0, headY + headSize / 2 + 0.18, 0.05)
    group.add(knot)
    const loopGeo = new THREE.BoxGeometry(0.12, 0.14, 0.05)
    const leftLoop = new THREE.Mesh(loopGeo, bowMat)
    leftLoop.position.set(-0.12, headY + headSize / 2 + 0.18, 0.05)
    group.add(leftLoop)
    const rightLoop = new THREE.Mesh(loopGeo, bowMat)
    rightLoop.position.set(0.12, headY + headSize / 2 + 0.18, 0.05)
    group.add(rightLoop)
    // веснушки
    const freckleMat = new THREE.MeshBasicMaterial({ color: 0xc96f3d })
    const freckleGeo = new THREE.BoxGeometry(0.03, 0.03, 0.02)
    const f1 = new THREE.Mesh(freckleGeo, freckleMat)
    f1.position.set(-eyeOffsetX - 0.03, headY - 0.05, eyeZ)
    group.add(f1)
    const f2 = new THREE.Mesh(freckleGeo, freckleMat)
    f2.position.set(eyeOffsetX + 0.03, headY - 0.05, eyeZ)
    group.add(f2)
  } else if (accessory === "headband") {
    const bandMat = new THREE.MeshToonMaterial({ color: 0xff5566 })
    const bandGeo = new THREE.BoxGeometry(headSize + 0.04, 0.06, headSize + 0.04)
    const band = new THREE.Mesh(bandGeo, bandMat)
    band.position.set(0, headY + headSize / 2 - 0.04, 0)
    group.add(band)
  }
}

function buildHair(hairKey: string, gender: "male" | "female", mat: any, headSize: number): any {
  const hair = new THREE.Group()
  const s = headSize
  const add = (width: number, height: number, depth: number, x: number, y: number, z: number, radius = 0.04, material = mat) => {
    const mesh = new THREE.Mesh(softBox(width, height, depth, radius), material)
    mesh.position.set(x, y, z)
    hair.add(mesh)
    return mesh
  }
  const addOrb = (radius: number, x: number, y: number, z: number, scaleX = 1, scaleY = 1, scaleZ = 1, material = mat) => {
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, 14, 8), material)
    mesh.position.set(x, y, z)
    mesh.scale.set(scaleX, scaleY, scaleZ)
    hair.add(mesh)
    return mesh
  }
  const addCrown = (material = mat, height = 0.14, y = 0.49) => {
    // Сплюснутый низкополигональный объём повторяет макушку и не создаёт
    // прямоугольной полки над лицом. Пряди глубоко входят в него, поэтому
    // при повороте головы не появляются щели.
    addOrb(s * 0.5, 0, s * y, 0, 1.08, height / 0.5, 1.06, material)
  }
  const addBang = (width: number, height: number, x: number, y: number, rotation = 0, material = mat) => {
    const lock = new THREE.Mesh(new THREE.ConeGeometry(s * 0.09, s * height, 6), material)
    lock.position.set(s * x, s * y, s * 0.535)
    lock.scale.set(width / 0.18, 1, 0.62)
    lock.rotation.z = rotation
    hair.add(lock)
    return lock
  }
  const addSideLock = (x: number, y: number, height: number, depth = 0.24, z = -0.04) => {
    return addOrb(s * 0.13, s * x, s * y, s * z, 0.62, height / 0.26, depth / 0.26)
  }
  const addTaperedLock = (
    width: number,
    height: number,
    depth: number,
    x: number,
    y: number,
    z: number,
    tilt = 0,
    lean = 0,
    material = mat,
  ) => {
    const lock = new THREE.Mesh(
      new THREE.CylinderGeometry(s * width * 0.46, s * width * 0.055, s * height, 9, 1, false),
      material,
    )
    lock.position.set(s * x, s * y, s * z)
    lock.scale.z = depth / Math.max(width, 0.01)
    lock.rotation.z = tilt
    lock.rotation.x = lean
    hair.add(lock)
    return lock
  }
  const addCurveLock = (points: Array<[number, number, number]>, tube = 0.045, material = mat) => {
    const curve = new THREE.CatmullRomCurve3(points.map(([x, y, z]) => new THREE.Vector3(s * x, s * y, s * z)))
    const lock = new THREE.Mesh(new THREE.TubeGeometry(curve, 10, s * tube, 6, false), material)
    hair.add(lock)
    return lock
  }
  const addCrownShell = (material = mat, radius = 0.56, centerY = 0.08, depth = 1, arc = 1.5) => {
    // Купол с округло-квадратным сечением повторяет фактическую форму головы:
    // обычная полусфера оставляла открытыми её углы, а прямоугольная оболочка
    // читалась как коробка. Superellipse закрывает углы, сохраняя мягкий верх.
    const radialSegments = 24
    const verticalSegments = 7
    const radiusX = s * radius * 1.05
    const radiusY = s * radius
    const radiusZ = s * radius * 1.05 * depth
    const positions: number[] = []
    const indices: number[] = []
    const signedRoundSquare = (value: number) => Math.sign(value) * Math.pow(Math.abs(value), 0.32)
    for (let row = 0; row <= verticalSegments; row += 1) {
      const theta = arc * row / verticalSegments
      const ring = Math.sin(theta)
      const y = s * centerY + radiusY * Math.cos(theta)
      for (let column = 0; column <= radialSegments; column += 1) {
        const phi = Math.PI * 2 * column / radialSegments
        positions.push(
          radiusX * ring * signedRoundSquare(Math.cos(phi)),
          y,
          -s * 0.015 + radiusZ * ring * signedRoundSquare(Math.sin(phi)),
        )
      }
    }
    for (let row = 0; row < verticalSegments; row += 1) {
      for (let column = 0; column < radialSegments; column += 1) {
        const a = row * (radialSegments + 1) + column
        const b = a + radialSegments + 1
        indices.push(a, a + 1, b, b, a + 1, b + 1)
      }
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
    geometry.setIndex(indices)
    geometry.computeVertexNormals()
    const shell = new THREE.Mesh(geometry, material)
    hair.add(shell)
    return shell
  }
  const addBackShell = (
    material = mat,
    bottomAngle = 2.1,
    radius = 0.56,
    centerY = 0.08,
    depth = 1,
    topAngle = 1.32,
  ) => {
    // Мягкая непрерывная масса только на затылке и у висков. Она закрывает
    // углы кубической головы в три четверти, но не заходит на лицо. Отдельные
    // пряди поверх неё сохраняют читаемый слоистый силуэт.
    const radialSegments = 18
    const verticalSegments = 6
    const radiusX = s * radius * 1.05
    const radiusY = s * radius
    const radiusZ = s * radius * 1.05 * depth
    const phiStart = Math.PI * 0.78
    const phiLength = Math.PI * 1.44
    const positions: number[] = []
    const indices: number[] = []
    const signedRoundSquare = (value: number) => Math.sign(value) * Math.pow(Math.abs(value), 0.32)
    for (let row = 0; row <= verticalSegments; row += 1) {
      const theta = topAngle + (bottomAngle - topAngle) * row / verticalSegments
      const ring = Math.sin(theta)
      const y = s * centerY + radiusY * Math.cos(theta)
      for (let column = 0; column <= radialSegments; column += 1) {
        const phi = phiStart + phiLength * column / radialSegments
        positions.push(
          radiusX * ring * signedRoundSquare(Math.cos(phi)),
          y,
          -s * 0.015 + radiusZ * ring * signedRoundSquare(Math.sin(phi)),
        )
      }
    }
    for (let row = 0; row < verticalSegments; row += 1) {
      for (let column = 0; column < radialSegments; column += 1) {
        const a = row * (radialSegments + 1) + column
        const b = a + radialSegments + 1
        indices.push(a, a + 1, b, b, a + 1, b + 1)
      }
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
    geometry.setIndex(indices)
    geometry.computeVertexNormals()
    const shell = new THREE.Mesh(geometry, material)
    hair.add(shell)
    return shell
  }
  const addTempleCoverage = (
    height: number,
    leftMaterial = mat,
    rightMaterial = mat,
    width = 0.14,
    z = 0.19,
  ) => {
    // Короткие височные пряди закрывают боковые углы головы в ракурсе
    // три четверти. Они лежат снаружи оболочки и сужаются вниз, поэтому край
    // не выглядит ни лысым, ни закрытым тяжёлой прямоугольной панелью.
    const y = 0.34 - height * 0.5
    addTaperedLock(width, height, 0.11, -0.535, y, z, -0.055, 0, leftMaterial)
    addTaperedLock(width, height, 0.11, 0.535, y, z, 0.055, 0, rightMaterial)
  }

  // Два многослойных силуэта доступны всем персонажам. Общая геометрия
  // гарантирует одинаково аккуратную посадку, а цвет по-прежнему берётся из
  // выбранной пользователем палитры волос.
  if (hairKey === "shag" || hairKey === "wolf") {
    const shadeMat = mat.clone()
    shadeMat.color = mat.color.clone().multiplyScalar(0.76)
    const softShadeMat = mat.clone()
    softShadeMat.color = mat.color.clone().multiplyScalar(0.9)
    const isWolf = hairKey === "wolf"

    if (gender === "female") {
      // Женские шэг и wolf-cut строятся из одной округлой макушки и отдельных
      // сужающихся слоёв. Здесь намеренно нет больших боковых сфер: раньше они
      // входили друг в друга и превращали wolf-cut в цепочку серых шаров.
      addCrownShell(shadeMat, isWolf ? 0.58 : 0.56, 0.075, isWolf ? 1.03 : 1, isWolf ? 1.52 : 1.48)
      addBackShell(shadeMat, isWolf ? 2.28 : 2.05, isWolf ? 0.58 : 0.56, 0.075, isWolf ? 1.03 : 1)

      const bangs = isWolf
        ? [
            [-0.33, 0.31, 0.3, -0.13, shadeMat],
            [-0.11, 0.31, 0.39, -0.045, mat],
            [0.11, 0.32, 0.37, 0.045, softShadeMat],
            [0.33, 0.31, 0.29, 0.13, shadeMat],
          ]
        : [
            [-0.38, 0.32, 0.27, -0.14, shadeMat],
            [-0.23, 0.31, 0.34, -0.09, mat],
            [-0.08, 0.3, 0.39, -0.03, softShadeMat],
            [0.08, 0.3, 0.37, 0.03, mat],
            [0.23, 0.31, 0.33, 0.09, softShadeMat],
            [0.38, 0.32, 0.26, 0.14, shadeMat],
          ]
      for (const [x, y, height, tilt, material] of bangs as Array<[number, number, number, number, any]>) {
        addTaperedLock(0.16, height, 0.12, x, y, 0.48, tilt, -0.04, material)
      }
      addTempleCoverage(isWolf ? 0.64 : 0.52, shadeMat, softShadeMat, isWolf ? 0.155 : 0.145, 0.2)

      for (const side of [-1, 1]) {
        addTaperedLock(
          isWolf ? 0.2 : 0.18,
          isWolf ? 0.58 : 0.5,
          0.17,
          side * 0.54,
          0.1,
          0.02,
          side * 0.08,
          0,
          side < 0 ? shadeMat : softShadeMat,
        )
        addTaperedLock(
          isWolf ? 0.22 : 0.2,
          isWolf ? 0.94 : 0.76,
          0.17,
          side * 0.57,
          isWolf ? -0.22 : -0.13,
          -0.04,
          side * 0.1,
          0,
          side < 0 ? mat : shadeMat,
        )
      }

      const backLayers = isWolf
        ? [[-0.3, -0.2, 0.82, -0.1], [0, -0.25, 0.94, 0], [0.3, -0.2, 0.82, 0.1]]
        : [[-0.3, -0.11, 0.68, -0.08], [0, -0.15, 0.74, 0], [0.3, -0.11, 0.68, 0.08]]
      for (const [x, y, height, tilt] of backLayers as Array<[number, number, number, number]>) {
        addTaperedLock(0.21, height, 0.16, x, y, -0.58, tilt, 0, x === 0 ? softShadeMat : mat)
      }
      hair.userData.headwearTop = s * (isWolf ? 0.77 : 0.72)
      return hair
    }

    addCrown(shadeMat, isWolf ? 0.2 : 0.17, isWolf ? 0.5 : 0.47)
    addOrb(s * 0.39, 0, s * 0.27, -s * 0.25, 1.2, isWolf ? 1.45 : 1.05, 0.72, mat)
    const fringe = isWolf
      ? [
          [-0.36, 0.31, 0.35, -0.18, shadeMat],
          [-0.12, 0.33, 0.42, -0.07, mat],
          [0.12, 0.34, 0.39, 0.07, softShadeMat],
          [0.36, 0.31, 0.34, 0.18, shadeMat],
        ]
      : [
          [-0.42, 0.31, 0.31, -0.18, shadeMat],
          [-0.28, 0.3, 0.36, -0.12, mat],
          [-0.14, 0.3, 0.41, -0.06, softShadeMat],
          [0, 0.29, 0.44, 0, mat],
          [0.14, 0.3, 0.41, 0.06, softShadeMat],
          [0.28, 0.3, 0.36, 0.12, mat],
          [0.42, 0.31, 0.31, 0.18, shadeMat],
        ]
    for (const [x, y, height, tilt, material] of fringe as Array<[number, number, number, number, any]>) {
      const lock = addBang(0.2, height, x, y, tilt, material)
      // Конусы должны входить в передний объём макушки. При касании по
      // границе чёлка отрывалась от головы на строгом боковом ракурсе.
      lock.position.z -= s * 0.09
    }
    if (!isWolf) {
      // Второй ряд коротких прядей убирает эффект ровной «зубчатой» линии:
      // чёлка остаётся частой, но кончики ложатся на разной высоте, как в
      // референсе со слоистым шэгом.
      for (const [x, y, height, tilt, material] of [
        [-0.35, 0.39, 0.26, -0.12, softShadeMat],
        [-0.21, 0.4, 0.3, -0.08, shadeMat],
        [-0.07, 0.38, 0.34, -0.03, mat],
        [0.07, 0.39, 0.31, 0.03, softShadeMat],
        [0.21, 0.4, 0.29, 0.08, mat],
        [0.35, 0.39, 0.25, 0.12, shadeMat],
      ] as Array<[number, number, number, number, any]>) {
        const layer = addBang(0.13, height, x, y, tilt, material)
        layer.position.z -= s * 0.065
      }
    }
    const sideY = isWolf ? -0.18 : -0.1
    const sideHeight = isWolf ? 1.12 : 1.04
    const sideX = isWolf ? 0.48 : 0.47
    // В профиль одной длинной пряди недостаточно: её верх касался макушки
    // лишь в одной точке и из-за округлой геометрии выглядел висящим в
    // воздухе. Височные объёмы дают явное перекрытие с макушкой и прядями,
    // а увеличенная глубина удерживает цельный силуэт при повороте головы.
    for (const x of [-1, 1]) {
      addOrb(s * 0.16, s * x * 0.43, s * 0.22, -s * 0.03, 0.82, 1.2, 1.48, softShadeMat)
    }
    addSideLock(-sideX, sideY, sideHeight, 0.34, -0.03).rotation.z = -0.12
    addSideLock(sideX, sideY, sideHeight, 0.34, -0.03).rotation.z = 0.12
    for (const [x, y, height, tilt] of (isWolf
      ? [[-0.3, -0.2, 0.82, -0.14], [0, -0.26, 0.94, 0], [0.3, -0.2, 0.82, 0.14]]
      : [[-0.3, -0.11, 0.7, -0.12], [0, -0.14, 0.76, 0], [0.3, -0.11, 0.7, 0.12]]) as Array<[number, number, number, number]>) {
      const lock = new THREE.Mesh(new THREE.ConeGeometry(s * 0.13, s * height, 7), x === 0 ? softShadeMat : mat)
      lock.position.set(s * x, s * y, -s * 0.43)
      lock.rotation.z = tilt
      lock.scale.z = 0.7
      hair.add(lock)
    }
    hair.userData.headwearTop = s * (isWolf ? 0.75 : 0.69)
    return hair
  }

  if (gender === "female") {
    const shadeMat = mat.clone()
    shadeMat.color = mat.color.clone().multiplyScalar(0.76)
    const softShadeMat = mat.clone()
    softShadeMat.color = mat.color.clone().multiplyScalar(0.9)
    const highlightMat = mat.clone()
    highlightMat.color = mat.color.clone().multiplyScalar(1.2)

    const addCap = (height = 1, depth = 1, parted = true) => {
      const shell = addCrownShell(shadeMat, 0.56 * height, 0.08, 1.01 * depth, parted ? 1.48 : 1.52)
      addBackShell(
        shadeMat,
        hairKey === "long" ? 2.24 : hairKey === "curly" ? 1.9 : hairKey === "bun" ? 1.72 : 1.64,
        0.56 * height,
        0.08,
        1.01 * depth,
      )
      // Тонкие направляющие пряди разбивают сплошную массу и показывают
      // направление волос, не создавая прямоугольных рёбер на макушке.
      for (const [x, material] of [[-0.32, shadeMat], [-0.17, mat], [0.17, softShadeMat], [0.32, mat]] as Array<[number, any]>) {
        addCurveLock([
          [parted ? Math.sign(x) * 0.035 : x * 0.2, 0.61 * height, 0.08],
          [x * 0.72, 0.56 * height, 0.18],
          [x, 0.42 * height, 0.24],
        ], 0.018, material)
      }
      return shell
    }
    const addFrontLayers = (short = false) => {
      const layers: Array<[number, number, number, number, number, any]> = short
        ? [[-0.29, 0.27, 0.38, 0.14, -0.08, shadeMat], [-0.03, 0.235, 0.37, 0.17, -0.025, mat], [0.25, 0.29, 0.34, 0.12, 0.08, softShadeMat]]
        : [[-0.29, 0.255, 0.38, 0.16, -0.07, shadeMat], [-0.03, 0.225, 0.38, 0.19, -0.02, mat], [0.25, 0.275, 0.35, 0.14, 0.07, softShadeMat]]
      for (const [x, y, width, height, tilt, material] of layers) {
        addTaperedLock(width * 0.62, height * 1.7, 0.12, x, y, 0.48, tilt, -0.04, material)
      }
    }

    if (hairKey === "long") {
      addCap(1.02, 1.03)
      addFrontLayers()
      addTempleCoverage(0.7, shadeMat, softShadeMat, 0.16)
      // Пять отдельных прядей формируют затылок и длину до плеч. Их верхние
      // части перекрывают оболочку, а сужающиеся концы дают живой силуэт.
      for (const [x, y, height, width, tilt, material] of [
        [-0.34, -0.12, 0.88, 0.25, -0.045, shadeMat],
        [-0.17, -0.17, 1.0, 0.26, -0.025, mat],
        [0, -0.2, 1.06, 0.27, 0, softShadeMat],
        [0.17, -0.17, 1.0, 0.26, 0.025, mat],
        [0.34, -0.12, 0.88, 0.25, 0.045, shadeMat],
      ] as Array<[number, number, number, number, number, any]>) {
        addTaperedLock(width, height, 0.18, x, y, -0.43, tilt, 0, material)
      }
      addTaperedLock(0.26, 0.82, 0.19, -0.51, -0.08, 0.025, -0.035, 0, shadeMat)
      addTaperedLock(0.26, 0.82, 0.19, 0.51, -0.08, 0.025, 0.035, 0, shadeMat)
    } else if (hairKey === "bun") {
      addCap(1.01, 1.02)
      addFrontLayers()
      addTempleCoverage(0.4, shadeMat, softShadeMat, 0.14)
      addTaperedLock(0.18, 0.44, 0.13, -0.49, 0.08, 0.02, -0.03, 0, shadeMat)
      addTaperedLock(0.18, 0.44, 0.13, 0.49, 0.08, 0.02, 0.03, 0, shadeMat)
      const bunBase = addOrb(s * 0.17, 0, s * 0.63, -s * 0.08, 1.08, 0.68, 0.86, shadeMat)
      bunBase.rotation.x = 0.18
      const knot = new THREE.Mesh(new THREE.TorusKnotGeometry(s * 0.16, s * 0.052, 28, 5, 2, 3), mat)
      knot.position.set(0, s * 0.79, -s * 0.04)
      knot.scale.set(1.26, 1.08, 0.78)
      knot.rotation.x = Math.PI * 0.5
      hair.add(knot)
    } else if (hairKey === "curly") {
      addCap(0.96, 1.02, false)
      addFrontLayers(true)
      addTempleCoverage(0.44, shadeMat, softShadeMat, 0.145)
      // S-образные открытые пряди читаются как кудри и не образуют круглые
      // бусины или замкнутые петли вокруг головы.
      addCurveLock([[-0.36, 0.46, 0.42], [-0.5, 0.36, 0.55], [-0.37, 0.22, 0.57], [-0.5, 0.08, 0.5]], 0.05, shadeMat)
      addCurveLock([[0.36, 0.46, 0.42], [0.5, 0.36, 0.55], [0.37, 0.22, 0.57], [0.5, 0.08, 0.5]], 0.05, mat)
      addCurveLock([[-0.48, 0.32, 0.05], [-0.57, 0.18, 0.18], [-0.48, 0.02, 0.2], [-0.56, -0.12, 0.12]], 0.052, mat)
      addCurveLock([[0.48, 0.32, 0.05], [0.57, 0.18, 0.18], [0.48, 0.02, 0.2], [0.56, -0.12, 0.12]], 0.052, shadeMat)
      addCurveLock([[-0.34, 0.42, -0.35], [-0.45, 0.28, -0.48], [-0.31, 0.12, -0.5], [-0.42, -0.05, -0.43]], 0.05, shadeMat)
      addCurveLock([[0, 0.46, -0.42], [0.12, 0.32, -0.53], [-0.06, 0.15, -0.54], [0.1, -0.04, -0.46]], 0.052, mat)
      addCurveLock([[0.34, 0.42, -0.35], [0.45, 0.28, -0.48], [0.31, 0.12, -0.5], [0.42, -0.05, -0.43]], 0.05, softShadeMat)
    } else {
      addCap(0.96, 1)
      addFrontLayers(true)
      addTempleCoverage(0.32, shadeMat, softShadeMat, 0.13)
      // Короткая причёска собрана слоями: тонкие виски, затылочная линия и
      // асимметричная чёлка вместо одинаковой круглой крышки.
      addTaperedLock(0.17, 0.35, 0.13, -0.48, 0.16, 0.04, -0.05, 0, shadeMat)
      addTaperedLock(0.17, 0.31, 0.13, 0.48, 0.18, 0.04, 0.05, 0, shadeMat)
      for (const [x, tilt] of [[-0.28, -0.035], [-0.09, -0.015], [0.1, 0.015], [0.29, 0.035]] as Array<[number, number]>) {
        addTaperedLock(0.19, 0.3, 0.14, x, 0.22, -0.44, tilt, 0, x === 0.1 ? softShadeMat : mat)
      }
    }

    hair.userData.headwearTop = s * (hairKey === "bun" ? 1.08 : hairKey === "curly" ? 0.72 : 0.68)
    return hair
  }

  if (hairKey === "long") {
    addCrown()
    addBang(0.24, 0.17, -0.15, 0.36, -0.16)
    addBang(0.24, 0.17, 0.15, 0.36, 0.16)
    // Цельная округлая масса сзади даёт читаемую длину до плеч и не
    // распадается сверху на прямоугольные блоки или отдельные «пальцы».
    addOrb(s * 0.2, 0, -s * 0.12, -s * 0.5, 2.25, 2.75, 0.52)
    addOrb(s * 0.14, -s * 0.48, -s * 0.17, -s * 0.03, 0.72, 3.75, 0.78)
    addOrb(s * 0.14, s * 0.48, -s * 0.17, -s * 0.03, 0.72, 3.75, 0.78)
  } else if (hairKey === "bun") {
    addCrown()
    addBang(0.34, 0.13, -0.12, 0.39, -0.14)
    addBang(0.18, 0.11, 0.2, 0.4, 0.12)
    // Пучок поднят и вынесен назад, чтобы он был заметен и спереди, и
    // в игровом ракурсе. Маленькое основание визуально соединяет его с волосами.
    addOrb(s * 0.17, 0, s * 0.56, -s * 0.21, 1.06, 0.76, 1)
    addOrb(s * 0.25, 0, s * 0.67, -s * 0.27, 1.06, 0.96, 1)
    addSideLock(-0.47, 0.18, 0.34, 0.14, 0.22)
    addSideLock(0.47, 0.18, 0.34, 0.14, 0.22)
  } else if (hairKey === "curly") {
    addCrown(mat, 0.1, 0.48)
    // Кудри — самостоятельные низкополигональные локоны вокруг головы.
    // Они перекрываются с основой и формируют объёмный неровный контур.
    for (const [x, y, z, r] of [
      [-0.38, 0.49, 0.28, 0.2], [0, 0.54, 0.34, 0.21], [0.38, 0.49, 0.28, 0.2],
      [-0.48, 0.3, 0.18, 0.18], [0.48, 0.3, 0.18, 0.18],
      [-0.46, 0.33, -0.2, 0.2], [0.46, 0.33, -0.2, 0.2],
      [-0.3, 0.48, -0.36, 0.21], [0.08, 0.5, -0.4, 0.22], [0.36, 0.45, -0.34, 0.2],
      [-0.47, 0.1, 0.08, 0.16], [0.47, 0.1, 0.08, 0.16],
    ] as Array<[number, number, number, number]>) {
      addOrb(s * r, s * x, s * y, s * z, 1, 0.94, 1)
    }
  } else if (hairKey === "cap") {
    const capMat = new THREE.MeshToonMaterial({ color: 0x1e3a8a })
    // Волосы под кепкой видны только тонкой чёлкой и у висков.
    addBang(0.18, 0.12, -0.19, 0.32, -0.12)
    addBang(0.18, 0.12, 0.19, 0.32, 0.12)
    addSideLock(-0.49, 0.14, 0.3, 0.19, -0.03)
    addSideLock(0.49, 0.14, 0.3, 0.19, -0.03)
    addOrb(s * 0.47, 0, s * 0.39, 0, 1.12, 0.52, 1.05, capMat)
    const brim = add(s * 0.78, s * 0.065, s * 0.48, 0, s * 0.31, s * 0.59, s * 0.03, capMat)
    brim.rotation.x = -0.04
    addOrb(s * 0.05, 0, s * 0.65, -s * 0.02, 1, 0.7, 1, capMat)
  } else if (hairKey === "short") {
    // Короткая стрижка прилегает к голове; узнаваемость создают лёгкая
    // чёлка и аккуратные виски, а не тяжёлый прямоугольный шлем.
    addCrown()
    addBang(0.18, 0.11, -0.16, 0.4, -0.14)
    addBang(0.14, 0.09, 0.12, 0.42, 0.1)
    addSideLock(-0.48, 0.23, 0.25, 0.14, 0.07)
    addSideLock(0.48, 0.23, 0.25, 0.14, 0.07)
    add(s * 0.52, s * 0.14, s * 0.13, 0, s * 0.32, -s * 0.5, s * 0.045)
  } else {
    // Ёжик — тонкая стрижка по форме головы с короткими заметными
    // прядями наверху. Он не повторяет обычные «Короткие».
    addCrown(mat, 0.075, 0.49)
    for (const [x, z, tilt] of [[-0.32, 0.18, -0.12], [0, 0.22, 0], [0.32, 0.18, 0.12], [-0.24, -0.18, -0.08], [0.12, -0.2, 0.06], [0.36, -0.14, 0.12]] as Array<[number, number, number]>) {
      const spike = new THREE.Mesh(new THREE.ConeGeometry(s * 0.075, s * 0.16, 5), mat)
      spike.position.set(s * x, s * 0.585, s * z)
      spike.rotation.z = tilt
      hair.add(spike)
    }
    addBang(0.2, 0.08, -0.11, 0.44, -0.08)
    addBang(0.2, 0.08, 0.11, 0.44, 0.08)
  }
  hair.userData.headwearTop = s * (
    hairKey === "bun" ? 0.92
      : hairKey === "curly" ? 0.76
        : hairKey === "cap" ? 0.7
          : hairKey === "buzz" ? 0.68
            : 0.64
  )
  return hair
}

// Спрайт с именем, городом, учебой и текущей задачей
export function createNameSprite(
  name: string,
  city: string,
  task: string = "",
  studyType: "SCHOOL" | "COLLEGE" | null = null,
  studyValue: string = "",
): any {
  // Линия 1: «Имя, Город»
  const line1 = city ? `${name}, ${city}` : name

  // Линия 2: «🏫 Школа, 11 класс» / «🎓 Колледж, ИСП-201»
  const line2 = studyType && studyValue
    ? studyType === "SCHOOL"
      ? `Школа, ${studyValue}`
      : `Колледж, ${studyValue}`
    : ""

  const canvas = document.createElement("canvas")
  canvas.width = 320
  const rowCount = (line2 ? 1 : 0) + (task ? 1 : 0)
  canvas.height = 46 + rowCount * 38
  const ctx = canvas.getContext("2d")!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Пузырь с «Имя, Город»
  drawRoundedRect(ctx, 8, 8, 304, 34, 17, "rgba(255,255,255,0.95)")
  ctx.fillStyle = "#2b2118"
  ctx.font = "bold 20px system-ui, sans-serif"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(trim(line1, 28), 160, 25)

  let nextY = 46

  if (line2) {
    const bgColor = studyType === "SCHOOL"
      ? "rgba(99, 176, 255, 0.95)"
      : "rgba(168, 130, 255, 0.95)"
    drawRoundedRect(ctx, 16, nextY, 288, 30, 14, bgColor)
    ctx.fillStyle = "#fff"
    ctx.font = "700 14px system-ui, sans-serif"
    ctx.fillText(trim(line2, 30), 160, nextY + 15)
    nextY += 38
  }

  if (task) {
    drawRoundedRect(ctx, 16, nextY, 288, 30, 14, "rgba(255, 206, 120, 0.95)")
    ctx.fillStyle = "#4d2411"
    ctx.font = "800 15px system-ui, sans-serif"
    ctx.fillText(trim(task, 30), 160, nextY + 15)
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.needsUpdate = true
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: true,
    depthWrite: false,
  })
  const sprite = new THREE.Sprite(material)
  const h = 0.34 + rowCount * 0.2
  sprite.scale.set(1.5, h, 1)
  sprite.renderOrder = 40
  return sprite
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
) {
  ctx.fillStyle = fill
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
  ctx.fill()
}

function trim(text: string, max: number) {
  if (!text) return ""
  return text.length > max ? text.slice(0, max - 1) + "…" : text
}

export function buildCapybaraNPC(): CapybaraNPC {
  const group = new THREE.Group()
  group.userData.isNPC = true

  const bodyMat = new THREE.MeshToonMaterial({ color: 0x8B5E3C })
  const darkMat = new THREE.MeshToonMaterial({ color: 0x4A2E1A })
  const noseMat = new THREE.MeshToonMaterial({ color: 0x5E3520 })
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x1A0800 })

  const bodyGroup = new THREE.Group()

  // --- Туловище ---
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.56, 1.05), bodyMat)
  torso.position.set(0, 0.62, 0)
  torso.userData.isNPC = true
  bodyGroup.add(torso)

  // --- Передние лапы (опираются на стойку) ---
  const frontLegGeo = new THREE.BoxGeometry(0.23, 0.38, 0.24)
  ;[[-0.29, 0.34, 0.45], [0.29, 0.34, 0.45]].forEach(([lx, ly, lz]) => {
    const leg = new THREE.Mesh(frontLegGeo, bodyMat)
    leg.position.set(lx, ly, lz)
    leg.userData.isNPC = true
    bodyGroup.add(leg)
  })

  // Лапки (передние лапы лежат на стойке)
  const pawGeo = new THREE.BoxGeometry(0.26, 0.1, 0.34)
  ;[[-0.29, 0.56, 0.66], [0.29, 0.56, 0.66]].forEach(([lx, ly, lz]) => {
    const paw = new THREE.Mesh(pawGeo, darkMat)
    paw.position.set(lx, ly, lz)
    paw.userData.isNPC = true
    bodyGroup.add(paw)
  })

  // --- Задние лапы (поджаты) ---
  const backLegGeo = new THREE.BoxGeometry(0.27, 0.48, 0.28)
  ;[[-0.29, 0.32, -0.36], [0.29, 0.32, -0.36]].forEach(([lx, ly, lz]) => {
    const leg = new THREE.Mesh(backLegGeo, bodyMat)
    leg.position.set(lx, ly, lz)
    leg.userData.isNPC = true
    bodyGroup.add(leg)
  })

  // --- Голова ---
  const headGroup = new THREE.Group()

  const headMain = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.6, 0.82), bodyMat)
  headMain.userData.isNPC = true
  headGroup.add(headMain)

  // Морда (плоская прямоугольная морда)
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.57, 0.36, 0.32), noseMat)
  muzzle.position.set(0, -0.09, 0.48)
  muzzle.userData.isNPC = true
  headGroup.add(muzzle)

  // Ноздри
  const nostrilMat = new THREE.MeshBasicMaterial({ color: 0x1E0800 })
  const nostrilGeo = new THREE.BoxGeometry(0.11, 0.09, 0.06)
  ;[[-0.15, -0.06, 0.63], [0.15, -0.06, 0.63]].forEach(([nx, ny, nz]) => {
    const n = new THREE.Mesh(nostrilGeo, nostrilMat)
    n.position.set(nx, ny, nz)
    n.userData.isNPC = true
    headGroup.add(n)
  })

  // Глаза
  const eyeGeo = new THREE.BoxGeometry(0.1, 0.1, 0.05)
  ;[[-0.24, 0.12, 0.42], [0.24, 0.12, 0.42]].forEach(([ex, ey, ez]) => {
    const eye = new THREE.Mesh(eyeGeo, eyeMat)
    eye.position.set(ex, ey, ez)
    eye.userData.isNPC = true
    headGroup.add(eye)
  })

  // Уши
  const earGeo = new THREE.BoxGeometry(0.19, 0.2, 0.13)
  ;[[-0.29, 0.37, -0.14], [0.29, 0.37, -0.14]].forEach(([ex, ey, ez]) => {
    const ear = new THREE.Mesh(earGeo, darkMat)
    ear.position.set(ex, ey, ez)
    ear.userData.isNPC = true
    headGroup.add(ear)
  })

  // --- Очки ботаника (крупная чёрная оправа) ---
  const frameMat = new THREE.MeshToonMaterial({ color: 0x0A0A0A })
  const lensMat2 = new THREE.MeshBasicMaterial({ color: 0xCFE8FF, transparent: true, opacity: 0.35 })
  const frameGeo = new THREE.BoxGeometry(0.27, 0.2, 0.045)
  const lensGeo2 = new THREE.BoxGeometry(0.23, 0.16, 0.02)
  ;[[-0.23, 0.12], [0.23, 0.12]].forEach(([fx, fy]) => {
    const frame = new THREE.Mesh(frameGeo, frameMat)
    frame.position.set(fx, fy, 0.44)
    frame.userData.isNPC = true
    headGroup.add(frame)
    const lens = new THREE.Mesh(lensGeo2, lensMat2)
    lens.position.set(fx, fy, 0.455)
    lens.userData.isNPC = true
    headGroup.add(lens)
  })
  const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.045, 0.035), frameMat)
  bridge.position.set(0, 0.12, 0.435)
  bridge.userData.isNPC = true
  headGroup.add(bridge)

  headGroup.position.set(0, 1.22, 0.26)
  headGroup.userData.isNPC = true
  bodyGroup.add(headGroup)

  group.add(bodyGroup)

  // --- Спрайт с именем ---
  const labelCanvas = document.createElement('canvas')
  labelCanvas.width = 320
  labelCanvas.height = 62
  const lctx = labelCanvas.getContext('2d')!
  lctx.clearRect(0, 0, 320, 62)
  lctx.fillStyle = 'rgba(255, 243, 195, 0.97)'
  lctx.beginPath()
  if (lctx.roundRect) {
    lctx.roundRect(6, 5, 308, 48, 24)
  } else {
    lctx.rect(6, 5, 308, 48)
  }
  lctx.fill()
  lctx.fillStyle = '#4E2800'
  lctx.font = 'bold 24px system-ui, sans-serif'
  lctx.textAlign = 'center'
  lctx.textBaseline = 'middle'
  lctx.fillText('\uD83E\uDDB4 Куратор Капибара', 160, 29)
  const labelTex = new THREE.CanvasTexture(labelCanvas)
  labelTex.needsUpdate = true
  const labelSprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthTest: false })
  )
  labelSprite.scale.set(2.4, 0.6, 1)
  labelSprite.position.set(0, 2.15, 0)
  labelSprite.renderOrder = 999
  labelSprite.userData.isNPC = true
  group.add(labelSprite)

  function update(t: number) {
    const breathe = Math.sin(t * 0.85) * 0.013
    bodyGroup.position.y = breathe
    headGroup.rotation.x = Math.sin(t * 0.55) * 0.016
  }

  return { group, update }
}

// ─────────────────────────────────────────────────────────────────────────
// Обезьянка-бариста для кофейни
// ─────────────────────────────────────────────────────────────────────────
export function buildMonkeyBaristaNPC(): CapybaraNPC {
  const group = new THREE.Group()
  group.userData.isNPC = true

  const furMat = new THREE.MeshToonMaterial({ color: 0x6F4A2B })
  const faceMat = new THREE.MeshToonMaterial({ color: 0xE8C28A })
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 })
  const apronMat = new THREE.MeshToonMaterial({ color: 0x1F2E4E })
  const capMat = new THREE.MeshToonMaterial({ color: 0xBF3A2C })

  const bodyGroup = new THREE.Group()

  // Туловище в фартуке
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.8, 0.42), furMat)
  torso.position.set(0, 0.9, 0)
  torso.userData.isNPC = true
  bodyGroup.add(torso)

  const apron = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.66, 0.05), apronMat)
  apron.position.set(0, 0.88, 0.22)
  apron.userData.isNPC = true
  bodyGroup.add(apron)

  const apronBand = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.06, 0.04),
    new THREE.MeshToonMaterial({ color: 0xE6C547 }),
  )
  apronBand.position.set(0, 1.05, 0.24)
  apronBand.userData.isNPC = true
  bodyGroup.add(apronBand)

  // Руки (в т.ч. одна поднята к кофемашине)
  const armGeo = new THREE.BoxGeometry(0.16, 0.6, 0.18)
  const leftArm = new THREE.Mesh(armGeo, furMat)
  leftArm.position.set(-0.38, 0.9, 0)
  leftArm.userData.isNPC = true
  bodyGroup.add(leftArm)
  const rightArm = new THREE.Mesh(armGeo, furMat)
  rightArm.position.set(0.38, 1.0, 0.08)
  rightArm.rotation.x = -0.4
  rightArm.userData.isNPC = true
  bodyGroup.add(rightArm)

  // Ноги
  const legGeo = new THREE.BoxGeometry(0.18, 0.48, 0.2)
  ;[-0.18, 0.18].forEach((lx) => {
    const leg = new THREE.Mesh(legGeo, furMat)
    leg.position.set(lx, 0.3, 0.05)
    leg.userData.isNPC = true
    bodyGroup.add(leg)
  })

  // Хвост закрученный
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.4), furMat)
  tail1.position.set(-0.25, 0.55, -0.2)
  tail1.rotation.x = 0.4
  tail1.userData.isNPC = true
  bodyGroup.add(tail1)
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.38, 0.12), furMat)
  tail2.position.set(-0.25, 0.72, -0.42)
  tail2.userData.isNPC = true
  bodyGroup.add(tail2)

  // Голова
  const headGroup = new THREE.Group()
  const headMain = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.52, 0.5), furMat)
  headMain.userData.isNPC = true
  headGroup.add(headMain)

  const face = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.38, 0.08), faceMat)
  face.position.set(0, -0.03, 0.24)
  face.userData.isNPC = true
  headGroup.add(face)

  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.18, 0.12), faceMat)
  muzzle.position.set(0, -0.15, 0.3)
  muzzle.userData.isNPC = true
  headGroup.add(muzzle)

  const nose = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.05, 0.04),
    new THREE.MeshBasicMaterial({ color: 0x2A1208 }),
  )
  nose.position.set(0, -0.09, 0.35)
  nose.userData.isNPC = true
  headGroup.add(nose)

  const eyeGeo = new THREE.BoxGeometry(0.08, 0.08, 0.04)
  ;[[-0.12, 0.05], [0.12, 0.05]].forEach(([ex, ey]) => {
    const eye = new THREE.Mesh(eyeGeo, eyeMat)
    eye.position.set(ex, ey, 0.28)
    eye.userData.isNPC = true
    headGroup.add(eye)
  })

  // Уши
  const earGeo = new THREE.BoxGeometry(0.14, 0.22, 0.1)
  ;[-0.32, 0.32].forEach((ex) => {
    const ear = new THREE.Mesh(earGeo, furMat)
    ear.position.set(ex, 0.08, 0)
    ear.userData.isNPC = true
    headGroup.add(ear)
    const innerEar = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.15, 0.04), faceMat)
    innerEar.position.set(ex * 0.85, 0.08, 0.04)
    innerEar.userData.isNPC = true
    headGroup.add(innerEar)
  })

  // Кепка баристы
  const capTop = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.1, 0.5), capMat)
  capTop.position.set(0, 0.32, 0)
  capTop.userData.isNPC = true
  headGroup.add(capTop)
  const capPeak = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.04, 0.22), capMat)
  capPeak.position.set(0, 0.27, 0.32)
  capPeak.userData.isNPC = true
  headGroup.add(capPeak)
  const capStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.58, 0.03, 0.51),
    new THREE.MeshToonMaterial({ color: 0xF4E7C9 }),
  )
  capStripe.position.set(0, 0.26, 0)
  capStripe.userData.isNPC = true
  headGroup.add(capStripe)

  headGroup.position.set(0, 1.6, 0.06)
  headGroup.userData.isNPC = true
  bodyGroup.add(headGroup)

  group.add(bodyGroup)

  // Лейбл
  const labelCanvas = document.createElement('canvas')
  labelCanvas.width = 320
  labelCanvas.height = 62
  const lctx = labelCanvas.getContext('2d')!
  lctx.clearRect(0, 0, 320, 62)
  lctx.fillStyle = 'rgba(41, 43, 51, 0.97)'
  lctx.beginPath()
  if (lctx.roundRect) lctx.roundRect(6, 5, 308, 48, 24)
  else lctx.rect(6, 5, 308, 48)
  lctx.fill()
  lctx.fillStyle = '#FFFDF8'
  lctx.font = '800 25px system-ui, sans-serif'
  lctx.textAlign = 'center'
  lctx.textBaseline = 'middle'
  lctx.fillText('\u0411\u0430\u0440\u0438\u0441\u0442\u0430 \u041c\u043e\u0447\u0438', 160, 29)
  const labelTex = new THREE.CanvasTexture(labelCanvas)
  labelTex.needsUpdate = true
  const labelSprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthTest: false, toneMapped: false }),
  )
  labelSprite.scale.set(2.8, 0.7, 1)
  labelSprite.position.set(0, 2.3, 0)
  labelSprite.renderOrder = 999
  labelSprite.userData.isNPC = true
  group.add(labelSprite)

  function update(t: number) {
    const breathe = Math.sin(t * 1.0) * 0.012
    bodyGroup.position.y = breathe
    rightArm.rotation.x = -0.4 + Math.sin(t * 2.5) * 0.18
    headGroup.rotation.y = Math.sin(t * 0.7) * 0.08
  }

  return { group, update }
}

// ─────────────────────────────────────────────────────────────────────────
// Зебра-администратор для коворкинга
// ─────────────────────────────────────────────────────────────────────────
export function buildZebraAdminNPC(): CapybaraNPC {
  const group = new THREE.Group()
  group.userData.isNPC = true

  const whiteMat = new THREE.MeshToonMaterial({ color: 0xF5F2E8 })
  const blackMat = new THREE.MeshToonMaterial({ color: 0x1A1A1A })
  const maneMat = new THREE.MeshToonMaterial({ color: 0x2A2A2A })
  const jacketMat = new THREE.MeshToonMaterial({ color: 0x2E3E64 })
  const shirtMat = new THREE.MeshToonMaterial({ color: 0xE8EEFF })
  const muzzleMat = new THREE.MeshToonMaterial({ color: 0xE0D8C4 })
  const pantsMat = new THREE.MeshToonMaterial({ color: 0x1E2843 })
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x111111 })

  const bodyGroup = new THREE.Group()

  // Туловище в пиджаке
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.9, 0.45), jacketMat)
  torso.position.set(0, 0.95, 0)
  torso.userData.isNPC = true
  bodyGroup.add(torso)

  const shirt = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.35, 0.05), shirtMat)
  shirt.position.set(0, 1.15, 0.24)
  shirt.userData.isNPC = true
  bodyGroup.add(shirt)

  const tie = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.34, 0.04),
    new THREE.MeshToonMaterial({ color: 0xC23A2C }),
  )
  tie.position.set(0, 1.12, 0.27)
  tie.userData.isNPC = true
  bodyGroup.add(tie)

  // Полосы зебры на теле
  for (let i = 0; i < 4; i++) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.08, 0.46), blackMat)
    stripe.position.set(0, 0.6 + i * 0.22, 0)
    stripe.userData.isNPC = true
    bodyGroup.add(stripe)
  }

  // Руки
  const armGeo = new THREE.BoxGeometry(0.18, 0.68, 0.2)
  ;[-0.44, 0.44].forEach((ax) => {
    const arm = new THREE.Mesh(armGeo, jacketMat)
    arm.position.set(ax, 0.95, 0)
    arm.userData.isNPC = true
    bodyGroup.add(arm)
  })

  // Ноги (брюки)
  const legGeo = new THREE.BoxGeometry(0.22, 0.52, 0.22)
  ;[-0.18, 0.18].forEach((lx) => {
    const leg = new THREE.Mesh(legGeo, pantsMat)
    leg.position.set(lx, 0.3, 0)
    leg.userData.isNPC = true
    bodyGroup.add(leg)
  })

  // Голова (вытянутая)
  const headGroup = new THREE.Group()

  const headMain = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.56, 0.78), whiteMat)
  headMain.userData.isNPC = true
  headGroup.add(headMain)

  // Полосы лежат чуть выше верхней плоскости головы. Прежние объёмные полосы
  // пересекали геометрию головы и мерцали из-за борьбы глубины.
  for (let i = 0; i < 4; i++) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.025, 0.1), blackMat)
    stripe.position.set(0, 0.292, -0.25 + i * 0.16)
    stripe.userData.isNPC = true
    headGroup.add(stripe)
  }

  // Морда
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.32, 0.24), muzzleMat)
  muzzle.position.set(0, -0.1, 0.42)
  muzzle.userData.isNPC = true
  headGroup.add(muzzle)

  const nostrilGeo = new THREE.BoxGeometry(0.08, 0.05, 0.05)
  ;[-0.08, 0.08].forEach((nx) => {
    const n = new THREE.Mesh(nostrilGeo, blackMat)
    n.position.set(nx, -0.14, 0.54)
    n.userData.isNPC = true
    headGroup.add(n)
  })

  // Глаза
  const eyeGeoZ = new THREE.BoxGeometry(0.09, 0.09, 0.05)
  ;[-0.16, 0.16].forEach((ex) => {
    const eye = new THREE.Mesh(eyeGeoZ, eyeMat)
    eye.position.set(ex, 0.06, 0.28)
    eye.userData.isNPC = true
    headGroup.add(eye)
  })

  // Уши
  const earGeo = new THREE.BoxGeometry(0.12, 0.22, 0.14)
  ;[-0.18, 0.18].forEach((ex) => {
    const ear = new THREE.Mesh(earGeo, whiteMat)
    ear.position.set(ex, 0.34, -0.22)
    ear.userData.isNPC = true
    headGroup.add(ear)
  })

  // Грива
  for (let i = 0; i < 4; i++) {
    const mane = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.28, 0.12), maneMat)
    mane.position.set(0, 0.28 - i * 0.03, -0.32 + i * 0.06)
    mane.userData.isNPC = true
    headGroup.add(mane)
  }

  // Очки администраторские
  const frameMatZ = new THREE.MeshToonMaterial({ color: 0x0A0A0A })
  const lensMatZ = new THREE.MeshBasicMaterial({ color: 0xCFE8FF, transparent: true, opacity: 0.4 })
  const frameGeoZ = new THREE.BoxGeometry(0.18, 0.16, 0.03)
  const lensGeoZ = new THREE.BoxGeometry(0.15, 0.13, 0.02)
  ;[-0.16, 0.16].forEach((fx) => {
    const frame = new THREE.Mesh(frameGeoZ, frameMatZ)
    frame.position.set(fx, 0.06, 0.3)
    frame.userData.isNPC = true
    headGroup.add(frame)
    const lens = new THREE.Mesh(lensGeoZ, lensMatZ)
    lens.position.set(fx, 0.06, 0.315)
    lens.userData.isNPC = true
    headGroup.add(lens)
  })
  const bridgeG = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.025, 0.02), frameMatZ)
  bridgeG.position.set(0, 0.06, 0.3)
  bridgeG.userData.isNPC = true
  headGroup.add(bridgeG)

  headGroup.position.set(0, 1.72, 0)
  headGroup.userData.isNPC = true
  bodyGroup.add(headGroup)

  group.add(bodyGroup)

  // Лейбл
  const labelCanvas = document.createElement('canvas')
  labelCanvas.width = 360
  labelCanvas.height = 62
  const lctx = labelCanvas.getContext('2d')!
  lctx.clearRect(0, 0, 360, 62)
  lctx.fillStyle = 'rgba(41, 43, 51, 0.97)'
  lctx.beginPath()
  if (lctx.roundRect) lctx.roundRect(6, 5, 348, 48, 24)
  else lctx.rect(6, 5, 348, 48)
  lctx.fill()
  lctx.fillStyle = '#FFFDF8'
  lctx.font = '800 25px system-ui, sans-serif'
  lctx.textAlign = 'center'
  lctx.textBaseline = 'middle'
  lctx.fillText('\u0410\u0434\u043c\u0438\u043d\u0438\u0441\u0442\u0440\u0430\u0442\u043e\u0440 \u0417\u0435\u0431\u0440\u0430', 180, 29)
  const labelTex = new THREE.CanvasTexture(labelCanvas)
  labelTex.needsUpdate = true
  const labelSprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthTest: false, toneMapped: false }),
  )
  labelSprite.scale.set(3.1, 0.7, 1)
  labelSprite.position.set(0, 2.5, 0)
  labelSprite.renderOrder = 999
  labelSprite.userData.isNPC = true
  group.add(labelSprite)

  function update(t: number) {
    const breathe = Math.sin(t * 0.8) * 0.014
    bodyGroup.position.y = breathe
    headGroup.rotation.y = Math.sin(t * 0.6) * 0.18
    headGroup.rotation.x = Math.sin(t * 0.4) * 0.04
  }

  return { group, update }
}
